import { logger } from '@/utils/logger';
import { useRouter } from 'next/router';
import { env } from '@zopauk/nextjs-env';
import { useEffect, useRef } from 'react';
import type { ParsedUrlQuery } from 'node:querystring';
import { useSession, login } from '@zopauk/nextjs-auth';
import { useQueryMember } from '@/utils/queries/useQueryMember';
import { AmazonConnectChatWidget } from '@zopauk/nextjs-amazon-connect';
import { waitForElementById, closeChat, openChat } from '@/utils/helpers';

type ChatHandlerProps = {
  status: 'unauthenticated' | 'authenticated' | 'loading';
  query: ParsedUrlQuery;
};

const handleChat = async ({ status, query }: ChatHandlerProps) => {
  if (typeof query['open-chat'] === 'string') {
    if (status !== 'authenticated') {
      await login({
        callbackUrl: `${env('NEXT_PUBLIC_WEB_GALLERIA_URL')}/contact?open-chat`,
      });
      return;
    }

    waitForElementById('amazon-connect-chat-widget', () => {
      openChat();
    });
  }
};

export const AmazonConnect = () => {
  const router = useRouter();
  const { isReady, query } = router;
  const { status } = useSession();
  const memberQuery = useQueryMember();
  const previousStatus = useRef<typeof status>(status);

  /**
   * We need to wait for the router to be ready and
   * amazon connect element to be attached to the dom
   * before we can open the chat
   */
  useEffect(() => {
    if (!isReady || status === 'loading') {
      return;
    }

    handleChat({
      status,
      query,
    });
  }, [query, status, isReady]);

  /**
   * Monitor session status changes and close the chat widget
   * when the user transitions from authenticated to unauthenticated.
   * This prevents unauthenticated chats after session timeout.
   */
  useEffect(() => {
    if (previousStatus.current === 'authenticated' && status === 'unauthenticated') {
      logger.info({
        message: '[AmazonConnect] - Session changed from authenticated to unauthenticated, closing chat widget',
        id: 'amazon-connect-session-logout',
      });
      closeChat();

      // If user was on the contact page with open-chat, redirect to clean URL
      if (typeof query['open-chat'] === 'string') {
        router.push('/contact');
      }
    }
    previousStatus.current = status;
  }, [status, query, router]);

  /**
   * Prevent unauthenticated users from accessing the chat widget.
   * If user is unauthenticated and the widget exists, close it immediately.
   */
  useEffect(() => {
    if (status === 'unauthenticated') {
      const chatWidget = document.getElementById('amazon-connect-chat-widget');
      if (chatWidget) {
        logger.info({
          message: '[AmazonConnect] - Unauthenticated user detected with chat widget, closing',
          id: 'amazon-connect-unauthenticated-close',
        });
        closeChat();
      }
    }
  }, [status]);

  /**
   * Monitor chat disconnect and redirect to /contact when chat ends.
   * This ensures users are redirected from /contact?open-chat to /contact
   * when they close or end the chat.
   *
   * The chat widget sends a postMessage with 'amazon-connect-agent-end-chat'
   * when the user ends the chat, which we use to trigger the redirect.
   */
  useEffect(() => {
    if (!isReady || typeof query['open-chat'] !== 'string') {
      return;
    }

    let redirected = false;
    let chatWasOpen = false;

    const performRedirect = () => {
      if (redirected) {
        return;
      }
      redirected = true;

      logger.info({
        message: '[AmazonConnect] - Chat ended, closing widget and redirecting to /contact',
        id: 'amazon-connect-chat-ended',
      });

      // Close the widget before redirecting
      closeChat();

      router.push('/contact');
    };

    // Trusted origin for postMessage validation - messages come from the same origin
    const trustedOrigin = window.location.origin;

    // Listen for postMessage from the widget - this is the primary detection method
    const handlePostMessage = (event: MessageEvent) => {
      if (!chatWasOpen || redirected) {
        return;
      }

      // Validate message origin for security
      if (trustedOrigin && event.origin !== trustedOrigin) {
        logger.info({
          message: '[AmazonConnect] - Ignoring postMessage from untrusted origin',
          id: 'amazon-connect-untrusted-origin',
          origin: event.origin,
        });
        return;
      }

      const messageStr = typeof event.data === 'string' ? event.data : JSON.stringify(event.data || {});

      // Check for end/close/disconnect indicators in the message
      if (messageStr.includes('end-chat') || messageStr.includes('disconnect') || messageStr.includes('CHAT_ENDED')) {
        logger.info({
          message: '[AmazonConnect] - Detected chat ended via postMessage',
          id: 'amazon-connect-postmessage-ended',
          data: messageStr.substring(0, 100),
        });
        performRedirect();
      }
    };
    window.addEventListener('message', handlePostMessage);

    // Monitor for when chat opens and detect widget removal
    const monitorChatState = setInterval(() => {
      const iframe = document.querySelector('#amazon-connect-chat-widget iframe') as HTMLIFrameElement | null;
      const chatContainer = document.getElementById('amazon-connect-chat-widget');

      // Track when chat opens
      if (iframe && !chatWasOpen) {
        chatWasOpen = true;
        logger.info({
          message: '[AmazonConnect] - Chat opened, starting to monitor for close',
          id: 'amazon-connect-chat-opened',
        });
      }

      if (!chatWasOpen) {
        return;
      }

      // Fallback: Widget container or iframe removed from DOM
      if (!chatContainer || !iframe) {
        logger.info({
          message: '[AmazonConnect] - Widget removed from DOM',
          id: 'amazon-connect-widget-removed',
        });
        performRedirect();
      }
    }, 500);

    return () => {
      clearInterval(monitorChatState);
      window.removeEventListener('message', handlePostMessage);
    };
  }, [isReady, query, router]);

  return <AmazonConnectChatWidget displayName={memberQuery.data?.data?.firstName} logger={logger} />;
};
